<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Expense Categories</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                @click="openCreateDialog"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search categories..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), getData()"
                    @keydown.enter="getData"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.type"
                    :items="typeOptions"
                    label="Type"
                    clearable
                    @click:clear="(filter.type = null), getData()"
                    @input="getData"
                    prepend-inner-icon="mdi-shape-outline"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    clearable
                    @click:clear="(filter.is_active = null), getData()"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Category Name</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Description</th>
                      <th class="text-left">Status</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="categories.length">
                    <tr
                      v-for="(category, i) in categories"
                      :key="`category_${i}`"
                    >
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td>{{ category.name }}</td>
                      <td>
                        <v-chip
                          small
                          :color="category.type === 0 ? 'deep-purple' : 'blue'"
                          text-color="white"
                        >
                          {{ category.type === 0 ? "Direct" : "Indirect" }}
                        </v-chip>
                      </td>
                      <td style="max-width: 320px">
                        {{ category.description || "-" }}
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="category.is_active ? 'teal' : 'red'"
                          text-color="white"
                        >
                          {{ category.is_active ? "Active" : "Inactive" }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-2"
                              color="primary"
                              @click="openEditDialog(category)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this Category</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="askDelete(category)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this Category</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="6" class="text-center">
                        <v-alert type="info" text>
                          No expense categories found
                        </v-alert>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-if="paginationLength > 1"
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: green; font-size: 90px">
                mdi-check-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: green">Success!</h1>
                <br />
                <h3>{{ successMessage }}</h3>
                <br />
                <v-btn color="success" @click="successDialog = false">
                  OK
                </v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: orange; font-size: 90px">
                mdi-alert
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: orange">Confirm Delete</h1>
                <br />
                <h3>
                  Delete "{{ deleteTarget ? deleteTarget.name : "" }}"? Any
                  expense already filed under it will block this.
                </h3>
                <br />
                <v-btn
                  color="grey"
                  text
                  class="mr-2"
                  @click="deleteDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn color="error" @click="confirmDelete">Delete</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog -->
    <AppDialog
      :show.sync="dialog"
      max-width="600"
      persistent
      icon="mdi-cash-multiple"
      :title="isEditMode ? 'Edit Expense Category' : 'Add Expense Category'"
      :subtitle="isEditMode ? 'Update this category\'s details' : 'Create a category to organize direct and indirect expenses'"
      @close="closeDialog"
    >
      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="formData.name"
              label="Category Name *"
              placeholder="e.g. Electricity / Light Bill"
              :rules="[rules.required]"
              :error-messages="errors.name"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="formData.type"
              :items="typeOptions"
              label="Type *"
              :rules="[rules.requiredType]"
              :error-messages="errors.type"
              outlined
              prepend-icon="mdi-shape-outline"
              hint="Direct = tied to goods movement. Indirect = running overhead."
              persistent-hint
            ></v-select>
          </v-col>
          <v-col cols="12">
            <v-alert
              v-if="isEditMode && typeChanged"
              type="warning"
              text
              dense
            >
              Changing the type will also re-classify every expense already
              filed under this category.
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="formData.description"
              label="Description"
              :error-messages="errors.description"
              outlined
              rows="2"
              auto-grow
            ></v-textarea>
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="formData.is_active"
              :label="formData.is_active ? 'Active' : 'Inactive'"
              color="success"
              inset
            ></v-switch>
          </v-col>
        </v-row>
      </v-form>

      <template v-slot:actions>
        <v-btn
          color="primary"
          :loading="isSaving"
          :disabled="!valid || isSaving"
          @click="save"
        >
          {{ isEditMode ? "Update" : "Create" }}
        </v-btn>
      </template>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
export default {
  name: "ExpenseCategoriesPage",
  components: { AppDialog },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      categories: [],
      page: 1,
      paginationLength: 0,
      count: 0,
      dialog: false,
      successDialog: false,
      errorDialog: false,
      deleteDialog: false,
      successMessage: "",
      errorMessage: "",
      deleteTarget: null,
      isEditMode: false,
      valid: false,
      originalType: null,
      filter: {
        search_keyword: null,
        type: null,
        is_active: null,
      },
      formData: {
        id: null,
        name: "",
        type: 1,
        description: "",
        is_active: true,
      },
      typeOptions: [
        { text: "Direct", value: 0 },
        { text: "Indirect", value: 1 },
      ],
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      rules: {
        required: (value) => !!value || "This field is required",
        requiredType: (value) =>
          value === 0 || !!value || "This field is required",
      },
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    typeChanged() {
      return this.originalType !== null && this.originalType !== this.formData.type;
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          rowsPerPage: this.rowsPerPage,
          page: this.page,
        };
        if (this.filter.search_keyword) {
          params.search_keyword = this.filter.search_keyword;
        }
        if (this.filter.type === 0 || this.filter.type) {
          params.type = this.filter.type;
        }
        if (this.filter.is_active === 0 || this.filter.is_active) {
          params.is_active = this.filter.is_active;
        }

        const response = await axios.get("/expense_categories", { params });
        this.categories = response.data.data || [];
        this.count = response.data.count || 0;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.log(error);
        this.categories = [];
        this.errorMessage = "Failed to load expense categories.";
        this.errorDialog = true;
      }
      this.isLoading = false;
    },

    openCreateDialog() {
      this.isEditMode = false;
      this.originalType = null;
      this.formData = {
        id: null,
        name: "",
        type: 1,
        description: "",
        is_active: true,
      };
      this.$store.dispatch("errors/setErrors", {});
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    openEditDialog(category) {
      this.isEditMode = true;
      this.originalType = category.type;
      this.formData = {
        id: category.id,
        name: category.name,
        type: category.type,
        description: category.description,
        is_active: category.is_active,
      };
      this.$store.dispatch("errors/setErrors", {});
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    closeDialog() {
      this.dialog = false;
      this.$store.dispatch("errors/setErrors", {});
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      this.isSaving = true;
      this.$store.dispatch("errors/setErrors", {});
      try {
        const payload = {
          name: this.formData.name,
          type: this.formData.type,
          description: this.formData.description,
          is_active: this.formData.is_active,
        };

        if (this.isEditMode) {
          await axios.patch(
            `/expense_categories/${this.encryptIt(this.formData.id)}`,
            payload,
          );
          this.successMessage = "Category updated successfully";
        } else {
          await axios.post("/expense_categories", payload);
          this.successMessage = "Category created successfully";
        }

        this.successDialog = true;
        this.closeDialog();
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else {
            this.errorMessage =
              error.response.data.message || "Failed to save category";
            this.errorDialog = true;
          }
        }
      }
      this.isSaving = false;
    },

    askDelete(category) {
      this.deleteTarget = category;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteDialog = false;
      try {
        await axios.post(
          `/expense_categories/delete/${this.encryptIt(this.deleteTarget.id)}`,
        );
        this.successMessage = "Category deleted successfully";
        this.successDialog = true;
        this.getData();
      } catch (error) {
        console.log(error);
        // The API refuses the delete when expenses still reference it.
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Failed to delete category";
        this.errorDialog = true;
      }
      this.deleteTarget = null;
    },
  },
};
</script>
